import { useState, useEffect, useCallback } from 'react';

import { AutocompleteOption } from 'domains/core/components/Autocomplete/Autocomplete';
import { ContentItem } from 'domains/core/components/AutocompleteContent/AutocompleteContent';

export type ContentSelectionHookProps = {
    contentId: number | null;
    contentList: ContentItem[];
    onContentChange: (contentId: number | null, contentName: string) => void;
};

export const useContentSelection = ({ contentId, contentList, onContentChange }: ContentSelectionHookProps) => {
    const [contentInputValue, setContentInputValue] = useState('');
    const [selectedOption, setSelectedOption] = useState<AutocompleteOption<ContentItem> | null>(null);
    // Track if content ID exists but is not found in the list (e.g., unpublished content)
    const [isContentUnavailable, setIsContentUnavailable] = useState(false);

    // Set selected option when contentId changes
    useEffect(() => {
        if (contentId && contentList?.length) {
            const selectedItem = contentList.find((item) => item.id === contentId);
            if (selectedItem) {
                const option = {
                    index: contentList.findIndex((item) => item.id === contentId) + 1,
                    label: selectedItem.name,
                    value: selectedItem,
                };
                setSelectedOption(option);
                setContentInputValue(option.label);
                setIsContentUnavailable(false);
                return;
            } else {
                // Content ID exists but not found in list - content is unavailable (unpublished)
                setSelectedOption(null);
                setContentInputValue('');
                setIsContentUnavailable(true);
                return;
            }
        }

        // Clear selection if no matching item or null contentId
        setSelectedOption(null);
        setContentInputValue('');
        setIsContentUnavailable(false);
    }, [contentId, contentList]);

    const handleContentSelection = useCallback(
        (_e: React.SyntheticEvent, option: AutocompleteOption<ContentItem> | null) => {
            setSelectedOption(option);

            if (!option) {
                // Handle clearing the selection
                onContentChange(null, '');
                return;
            }

            // Let CreateNewDropDownItem's onClick handle index 0
            if (option.index !== 0 && option.value) {
                onContentChange(option.value.id, option.value.name);
            }
        },
        [onContentChange]
    );

    const handleInputChange = useCallback((_e: React.SyntheticEvent, value: string) => {
        setContentInputValue(value);
    }, []);

    return {
        contentInputValue,
        selectedOption,
        isContentUnavailable,
        unavailableContentId: isContentUnavailable ? contentId : null,
        handleContentSelection,
        handleInputChange,
    };
};
